import { Box, Button, Checkbox, VStack, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { usePasswordStrength } from "@equidefi/portals/hooks/usePasswordStrength";
import { useChangePassword } from "@equidefi/portals/hooks/useUsers";
import { Heading, Password, Text } from "@equidefi/ui";

import { ProfileNavButton, VaultContainer } from "@/pages/app/vault/v2/components";
import { buildForgotPasswordSchema } from "@/pages/login/reset";

type ChangePasswordFormValues = {
  password: string;
  confirmPassword: string;
};

type ApiError = {
  data?: {
    error?: string;
  };
};

const initialValues: ChangePasswordFormValues = {
  password: "",
  confirmPassword: "",
};

const ChangePassword = () => {
  useDocumentTitle(["Change Password"]);

  const navigate = useNavigate();
  const toast = useToast();

  const changePassword = useChangePassword();
  const [showPassword, setShowPassword] = useState(false);

  const { score, analyze, validate } = usePasswordStrength();

  const strengthPercent = typeof score === "number" ? Math.max(0, Math.min(100, (score / 4) * 100)) : 0;

  const onSubmit = async ({ password }: ChangePasswordFormValues, { resetForm }: { resetForm: () => void }) => {
    try {
      await changePassword.mutateAsync({ password });

      resetForm();

      toast({
        status: "success",
        description: "You successfully changed your password.",
      });
    } catch (e: unknown) {
      console.error(e);

      const error = e as ApiError;

      toast({
        status: "error",
        description: error.data?.error || "Something went wrong.",
      });
    }
  };

  const { errors, touched, getFieldProps, handleSubmit, isValid, setFieldValue } = useFormik<ChangePasswordFormValues>({
    initialValues,
    validationSchema: buildForgotPasswordSchema(validate),
    onSubmit,
    validateOnMount: true,
    validateOnChange: true,
  });

  return (
    <VaultContainer>
      <VStack align="stretch" spacing="4" w="full">
        <ProfileNavButton to="/vault/profile" layout="rtl">
          Back To Account
        </ProfileNavButton>

        <Box
          bg="white"
          borderRadius="lg"
          p="4"
          boxShadow="0 1px 2px rgba(0, 0, 0, 0.2)"
          border="1px solid"
          borderColor="gray.100">
          <Heading as="h1" fontSize="24px" lineHeight="28px" fontWeight="700" mb="2.5">
            Change Password
          </Heading>

          <Text m="0" fontSize="17px" lineHeight="26px">
            You can change the password for your EquiDeFi account here.
          </Text>
        </Box>

        <Box as="form" noValidate onSubmit={handleSubmit}>
          <Password
            label="Create a new password"
            placeholder=""
            error={errors.password}
            isInvalid={Boolean(errors.password && touched.password)}
            h="12"
            mb="6"
            showPasswordOverwrite={showPassword}
            {...getFieldProps("password")}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = (e.currentTarget as any).value;

              analyze(value);
              setFieldValue("password", value);
            }}
          />

          <Password
            label="Confirm new password"
            placeholder=""
            error={errors.confirmPassword}
            isInvalid={Boolean(errors.confirmPassword && touched.confirmPassword)}
            h="12"
            mb="2"
            {...getFieldProps("confirmPassword")}
            showPasswordOverwrite={showPassword}
          />

          <Box w="full" h="12px" bg="gray.100" borderRadius="full" overflow="hidden" mb="6">
            <Box h="full" w={`${strengthPercent}%`} bg="#6FA35D" borderRadius="full" transition="width 0.2s ease" />
          </Box>

          <Checkbox mb="8" isChecked={showPassword} onChange={() => setShowPassword((prev) => !prev)}>
            <Text m="0" fontSize="17px" lineHeight="24px">
              Show Passwords
            </Text>
          </Checkbox>

          <Button
            isLoading={changePassword.isLoading}
            isDisabled={!isValid}
            type="submit"
            h="12"
            w="full"
            fontSize="17px"
            fontWeight="700">
            Save Password
          </Button>
        </Box>
      </VStack>
    </VaultContainer>
  );
};

export default ChangePassword;
